// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Conduit — the bots. Solo is the product for one player, so these are a deliverable, not a stub.
//
// TWO REAL TIERS, AND THE GAP BETWEEN THEM IS A GATE:
//   STEADY  one-ply greedy on the mode's own evaluator. The par-setter.
//   SHARP   beam search over the 4! ORDERINGS of the round's fittings as well as their placements.
//
// The ordering search is the point. Forced use means the interesting decision is not "where does
// this fitting go" but "which fitting do I spend first", because every mark rewrites what is legal
// for the rest. A bot that only searched placements would be strong at a game nobody is playing —
// and worse, it would make the depth gate pass for the wrong reason.
//
// THE DEPTH GATE IS WHY THIS FILE MATTERS BEYOND SOLO. tests/balance.test.ts requires SHARP to beat
// STEADY by a wide margin in EVERY mode. A mode where a one-ply greedy is near-optimal has no
// decisions in it, and that is a cosmetic mode wearing an inverted sign — which is exactly what the
// adversarial panel caught in Flume's first draft, and why Flume now charges for off-path cells.

import { CULVERT, FILAMENT } from './pieces';
import { PER_ROUND, type Mode } from './modes';
import { Game, restore, snapshot, type Move, type Snapshot } from './game';
import { CELLS, VENT_COUNT, type Plate } from './plate';
import {
  buildGraph,
  longestRun,
  offPathCells,
  openEnds,
  sealedVents,
  ventComponentSizes,
  type WeepSet,
} from './score';

export interface Skill {
  readonly id: string;
  readonly name: string;
  /** Beam width over fitting orderings. 1 = pure greedy, one ordering (the dealt one). */
  readonly beam: number;
  /** Whether to search all 24 orderings of the round's four fittings. */
  readonly orderings: boolean;
  /** Deliberately blind to connectivity — the negative-gap control arm. Never seated in a game. */
  readonly blind?: boolean;
}

export const SKILLS: Record<string, Skill> = {
  steady: { id: 'steady', name: 'Steady', beam: 1, orderings: false },
  sharp: { id: 'sharp', name: 'Sharp', beam: 8, orderings: true },
  // CONTROL ARM ONLY. Scores marks placed and nothing else — no vents, no runs, no ends. If this
  // does NOT lose decisively, the typed half-edge constraint is decoration and the run has failed.
  blind: { id: 'blind', name: 'Blind', beam: 1, orderings: false, blind: true },
};

export const SKILL_IDS: readonly string[] = ['steady', 'sharp'];

/**
 * The heuristic a bot maximises. NOT the score function — a bot that maximised the final score at
 * every step would refuse to open the ends that reaching a new vent requires, and would sit in a
 * corner drawing loops.
 *
 * The difference from `scorePlate` is the whole craft here: open ends are valued at a FRACTION of
 * their end-of-run cost early on, rising as the run closes, because an open end mid-run is a
 * frontier and an open end at the finish is a debt.
 */
function evaluate(plate: Plate, mode: Mode, weeps: WeepSet, progress: number, blind: boolean): number {
  if (blind) {
    // Counts drawn cells and nothing else. No graph is ever built.
    let n = 0;
    for (let i = 0; i < CELLS; i++) if (plate.piece[i] >= 0) n++;
    return n;
  }

  const g = buildGraph(plate, weeps);
  const rc = longestRun(g, CULVERT);
  const rf = longestRun(g, FILAMENT);

  let v = 0;

  if (mode.ventsScore) {
    const sealed = sealedVents(g);
    let nSealed = 0;
    for (let i = 0; i < VENT_COUNT; i++) if (sealed[i]) nSealed++;
    v += nSealed * mode.claim[0];
    for (const k of ventComponentSizes(g)) v += mode.net[Math.min(k, mode.net.length - 1)] ?? 0;
    // Touching a vent pays nothing until it seals, so without a gradient toward the unsealed ones
    // the bot would never reach for a vent it cannot finish inside the current beam.
    v += (g.ventNode.reduce((n, x) => n + (x >= 0 ? 1 : 0), 0) - nSealed) * 1.5;
    // A DISTANCE-TO-UNSEALED-VENT GRADIENT WAS TRIED HERE AND MEASURED WORSE, so it is not here.
    // The story was good: NET only moves at the instant a vent seals, a four-mark beam rarely
    // contains that instant, so give the search something to climb. Measured over 24 seeds it cut
    // sealed vents from 4.7 to 3.5 and pushed the ends term from -20 to -28 — it bought reaching
    // with debt and never closed. Recorded rather than deleted because the reasoning will look just
    // as convincing to the next person, and the sim is the only thing that settled it.
  }

  v += (rc.length + rf.length) * mode.runPer;

  if (mode.offPath !== 0) {
    v += (offPathCells(plate, rc.path, CULVERT) + offPathCells(plate, rf.path, FILAMENT)) * mode.offPath;
  }

  // The taper: an open end costs a quarter of its face value at the start and full price at the end.
  v += openEnds(plate) * mode.ends * (0.25 + 0.75 * progress);

  let w = 0;
  for (let i = 0; i < weeps.length; i++) if (weeps[i]) w++;
  v += w * mode.weep;

  return v;
}

/** Trials go through the shared snapshot helper in game.ts — never through the player's undo. */
type Snap = Snapshot;
const snap = (g: Game): Snap => snapshot(g);

export interface Step {
  slot: number;
  rot: number;
  x: number;
  y: number;
  scrap: boolean;
}

/**
 * The single best (slot, cell, rotation) available right now.
 *
 * This is already an ORDERING decision — it picks which fitting to spend as well as where — which is
 * why STEADY is a real opponent rather than a stub. Exhaustive over the legal set, which is
 * frontier-shaped and small. Ties break on first-found in a fixed sweep order; a random tiebreak
 * would make the sim irreproducible.
 */
export function bestStep(g: Game, skill: Skill): Step | null {
  const progress = g.log.length / (g.mode.rounds * PER_ROUND);
  const before = snap(g);
  let best: Step | null = null;
  let bestV = -Infinity;
  for (const slot of g.available()) {
    for (const s of g.spots(slot)) {
      if (!g.place(slot, s.rot, s.x, s.y)) continue;
      const v = evaluate(g.plate, g.mode, g.weepSet, progress, !!skill.blind);
      restore(g, before);
      if (v > bestV) {
        bestV = v;
        best = { slot, rot: s.rot, x: s.x, y: s.y, scrap: false };
      }
    }
  }
  if (best) return best;
  // Nothing placeable at all — scrap whichever available fitting is provably stuck.
  for (const slot of g.available()) if (g.mustScrap(slot)) return { slot, rot: 0, x: -1, y: -1, scrap: true };
  return null;
}

const applyStep = (g: Game, s: Step): boolean => (s.scrap ? g.scrap(s.slot) : g.place(s.slot, s.rot, s.x, s.y));

/**
 * Play one whole round.
 *
 * STEADY takes the best step, four times — greedy, but greedy over slot AND placement.
 * SHARP keeps a BEAM of whole-round lines: at each of the four steps it expands the best few
 * candidate steps from each surviving line and re-ranks, so it can accept a locally worse placement
 * that leaves the round's remaining fittings somewhere to go. That is the decision the mode is about,
 * and it is why the depth gate can tell a real mode from a cosmetic one.
 */
function playRound(g: Game, skill: Skill): void {
  const startRound = g.round;

  if (skill.beam <= 1) {
    let guard = 0;
    while (!g.over && g.round === startRound && guard++ <= PER_ROUND) {
      const s = bestStep(g, skill);
      if (!s || !applyStep(g, s)) return;
    }
    return;
  }

  const before = snap(g);
  const progress = g.log.length / (g.mode.rounds * PER_ROUND);

  // A line is a sequence of steps from `before`. Lines are scored by replaying them, which is
  // cheaper than carrying a plate per line and keeps the search a pure function of the start state.
  let lines: Step[][] = [[]];

  for (let depth = 0; depth < PER_ROUND; depth++) {
    const scored: Array<{ line: Step[]; v: number }> = [];
    for (const line of lines) {
      restore(g, before);
      let ok = true;
      for (const s of line) if (!applyStep(g, s)) { ok = false; break; }
      if (!ok || g.over || g.round !== startRound) {
        // A completed or broken line stays in the pool at its final value.
        if (ok) scored.push({ line, v: evaluate(g.plate, g.mode, g.weepSet, progress, !!skill.blind) });
        continue;
      }
      const after = snap(g);
      const cands: Array<{ s: Step; v: number }> = [];
      for (const slot of g.available()) {
        for (const sp of g.spots(slot)) {
          if (!g.place(slot, sp.rot, sp.x, sp.y)) continue;
          cands.push({ s: { slot, rot: sp.rot, x: sp.x, y: sp.y, scrap: false }, v: evaluate(g.plate, g.mode, g.weepSet, progress, !!skill.blind) });
          restore(g, after);
        }
      }
      if (cands.length === 0) {
        for (const slot of g.available()) {
          if (g.mustScrap(slot)) { cands.push({ s: { slot, rot: 0, x: -1, y: -1, scrap: true }, v: -999 }); break; }
        }
      }
      cands.sort((a, b) => b.v - a.v);
      for (const c of cands.slice(0, skill.beam)) scored.push({ line: [...line, c.s], v: c.v });
    }
    if (scored.length === 0) break;
    scored.sort((a, b) => b.v - a.v);
    lines = scored.slice(0, skill.beam).map((x) => x.line);
  }

  restore(g, before);
  const chosen = lines[0] ?? [];
  for (const s of chosen) applyStep(g, s);
  // If the beam came up short (it should not), finish the round greedily rather than stall.
  let guard = 0;
  while (!g.over && g.round === startRound && guard++ <= PER_ROUND) {
    const s = bestStep(g, skill);
    if (!s || !applyStep(g, s)) break;
  }
}

/** Play a complete run and hand back the finished Game. */
export function playOut(g: Game, skill: Skill): Game {
  let guard = 0;
  while (!g.over && guard++ < 64) playRound(g, skill);
  return g;
}

export function runFor(mode: Mode, seed: number, skill: Skill): Game {
  return playOut(new Game(mode, seed), skill);
}

export type { Move };
